/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Grid, Skeleton, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyGetDocumentAppIdQuery, usePostDocumentGenarateMutation } from "@api/documents";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/store";
import { setLoanStatus, setLoanApplication } from "@store/slices/registerSlice";
import {
  setRepresentanteLegal,
  setAval,
  setIsLoading,
  setBofuTabsObj,
  setBofuStatusAll,
  setAvalConyuge,
  setAvalRegimenConyugal,
  setAvalesAdicionales,
} from "@store/slices/bofuSlice";
import { useLazyGetLoanApplicationByIdQuery, usePutApplicationCompletedMutation } from "@api/applications";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import {
  DocumentResponse,
  BofuTabObject,
  BofuTabDocument,
  BofuTabStatus,
  BofuTabPropietario,
  FORM_STATUS_ENUM,
  BofuTabRepresentante,
  BofuTabAvalAdicional,
  PersonaStructure,
} from "@interfaces/response";
import { DOCUMENTS_CATALOG, STATUS_CATALOG, BOFU_TABS_CONFIG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import {
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetTabsConfigQuery,
} from "@api/catalogs";
import { identifyPersons } from "@helpers/funciones";
import TabBofuButton from "@components/TabBofu/TabBofuButton";
import {
  generateTabLabel,
  generateDocumentConfig,
  identityDocumentsByPerson,
  validateDocumentStructure,
  filterDocumentsByGuarantee,
  generateGuaranteeDocs,
  generatePropietarioDocs,
  findDocumentValue,
  initializeBofuTabsObject,
  mapBofuTabEmpresaForms,
  mapBofuTabAvalForms,
  mapBofuTabRepresentanteForms,
} from "./functions";
import * as Sentry from "@sentry/react";
import { useLazyGetGuaranteesDataQuery } from "@api/garantiasApi";

const TabBofu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [isSuccessData, setIsSuccessData] = useState<boolean>(false);

  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const applicationData = useSelector((state: RootState) => state.register.application);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const personConyugeAval = useSelector((state: RootState) => state.bofu.aval_conyuge);
  const avalesAdicionales = useSelector((state: RootState) => state.bofu.avales_adicionales);
  const bofuTabsObj = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const isLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [createDocSolicitudCredito, createDocSolicitudCreditoMetadata] = usePostDocumentGenarateMutation();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [getEstadoCivil] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesQuery();

  const generateSolicitudCredito = async () => {
    try {
      if (!bofuTabsObj.empresa) {
        return;
      }

      const { doc_solicitud_credito } = bofuTabsObj.empresa ?? {};
      const empresaForms = bofuTabsObj.empresa.forms;

      if (!empresaForms.solicitud_credito.enabled) {
        return;
      }

      const empresaFormStatuses = [];
      if (empresaForms.solicitud_credito.enabled) {
        empresaFormStatuses.push(applicationData.loan_application_information_status);
      }
      if (empresaForms.referencias_personales) {
        empresaFormStatuses.push(applicationData.references_status);
      }
      if (empresaForms.informacion_bancaria) {
        empresaFormStatuses.push(applicationData.bank_information_status);
      }

      const all_forms_completed = empresaFormStatuses.every((status) => status === "COMPLETO");

      // descartar si no se han completado todos los formularios
      if (!all_forms_completed) {
        return;
      }

      // descartar si ya se ha validado
      if (doc_solicitud_credito?.validated === true || doc_solicitud_credito?.validated === null) {
        return;
      }

      // descartar si hay request en curso
      if (createDocSolicitudCreditoMetadata.isLoading) {
        return;
      }

      const payload_document = {
        loan_application_id: applicationData.id,
        document_type: "solicitud_credito",
      };
      await createDocSolicitudCredito(payload_document).unwrap();

      const payload_loan = {
        completed: true,
      };
      await putAapplicationCompleted({ id_sol: applicationData.id, body: payload_loan });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-create_solicitud_credito",
        },
      });
    }
  };

  const generateTabsStatusLabel = () => {
    let dictamen_new_status: BofuTabStatus | undefined;
    let empresa_new_status: BofuTabStatus | undefined;
    let aval_new_status: BofuTabStatus | undefined;
    let propietario_real_new_status: BofuTabStatus | undefined;
    let garantia_new_status: BofuTabStatus | undefined;
    let representante_new_status: BofuTabStatus | undefined;
    const isPFAE = applicationData.tax_systems?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;

    if (bofuTabsObj.dictamen.is_active) {
      const params_dictamen_status = {
        tab_documents: bofuTabsObj.dictamen.documents,
        other_documents: [],
      };
      dictamen_new_status = generateTabLabel(params_dictamen_status);
    }

    if (bofuTabsObj.empresa.is_active) {
      const empresaForms = bofuTabsObj.empresa.forms;
      const forms: FORM_STATUS_ENUM[] = [];

      if (empresaForms.solicitud_credito.enabled) {
        forms.push(applicationData.loan_application_information_status as FORM_STATUS_ENUM);
      }
      if (empresaForms.referencias_personales) {
        forms.push(applicationData.references_status as FORM_STATUS_ENUM);
      }
      if (empresaForms.informacion_bancaria) {
        forms.push(applicationData.bank_information_status as FORM_STATUS_ENUM);
      }

      const params_empresa_status = {
        tab_documents: bofuTabsObj.empresa.documents,
        other_documents: [],
        forms,
      };

      empresa_new_status = generateTabLabel(params_empresa_status);
    }

    if (bofuTabsObj.aval.is_active) {
      const params_aval_status = {
        tab_documents: [] as BofuTabDocument[],
        other_documents: [] as (DocumentResponse | undefined)[],
        forms: [] as FORM_STATUS_ENUM[],
      };

      if (bofuTabsObj.aval.show_form_aval) {
        const avalForms = bofuTabsObj.aval.forms;

        // agregar los documentos del aval
        bofuTabsObj.aval.documents.forEach((item) => {
          params_aval_status.tab_documents.push(item);
        });

        if (avalForms.informacion_aval) {
          params_aval_status.forms.push(personAval?.personal_information_status as FORM_STATUS_ENUM);
        }

        if (avalForms.informacion_bancaria) {
          params_aval_status.forms.push(personAval?.bank_references_status as FORM_STATUS_ENUM);
        }

        if (avalForms.validacion_identidad) {
          params_aval_status.other_documents.push(bofuTabsObj.aval.doc_validacion_identidad);
        }
      }

      if (personConyugeAval) {
        bofuTabsObj.aval.documents_conyuge.forEach((item) => {
          params_aval_status.tab_documents.push(item);
        });

        params_aval_status.forms.push(personConyugeAval.personal_information_status);
      }

      if (avalesAdicionales.length) {
        avalesAdicionales.forEach((item) => {
          // agregar los documentos del aval adicional
          item.documents.forEach((docItem) => {
            params_aval_status.tab_documents.push(docItem);
          });

          // agregar el documento de validacion de identidad
          params_aval_status.other_documents.push(item.doc_validacion_identidad);

          // agregar los formularios del aval adicional
          params_aval_status.forms.push(item.persona.personal_information_status);
          params_aval_status.forms.push(item.persona.bank_references_status);
        });
      }

      aval_new_status = generateTabLabel(params_aval_status);
    }

    if (bofuTabsObj.propietarios_reales.is_active) {
      if (applicationData.decision_validated) {
        if (bofuTabsObj.propietarios_reales.propietarios.length) {
          const params_propietarios_reales_status = {
            tab_documents: bofuTabsObj.propietarios_reales.propietarios.flatMap(
              (item: BofuTabPropietario) => item.documents,
            ),
            other_documents: [] as (DocumentResponse | undefined)[],
            forms: [] as FORM_STATUS_ENUM[],
          };

          bofuTabsObj.propietarios_reales.propietarios.forEach((item) => {
            params_propietarios_reales_status.forms.push(item.persona.personal_information_status);
          });

          propietario_real_new_status = generateTabLabel(params_propietarios_reales_status);
        } else {
          propietario_real_new_status = "no_aplica";
        }
      } else {
        propietario_real_new_status = undefined;
      }
    }

    if (bofuTabsObj.garantia.is_active) {
      if (bofuTabsObj.garantia.items.length) {
        const params_garantia_status = {
          tab_documents: bofuTabsObj.garantia.items.flatMap((item) => item.documents),
          other_documents: [],
        };
        garantia_new_status = generateTabLabel(params_garantia_status);
      } else {
        garantia_new_status = "no_aplica";
      }
    }

    if (bofuTabsObj.representantes.is_active) {
      if (applicationData.decision_validated || isPFAE) {
        const params_representantes_status = {
          tab_documents: bofuTabsObj.representantes.items.flatMap((item) => item.documents),
          other_documents: [] as (DocumentResponse | undefined)[],
          forms: [] as FORM_STATUS_ENUM[],
        };

        const representanteForms = bofuTabsObj.representantes.forms;

        bofuTabsObj.representantes.items.forEach((item) => {
          if (item.show_information_form && representanteForms.informacion_representante_legal) {
            params_representantes_status.forms.push(item.persona.personal_information_status);
          }

          if (item.show_information_form && representanteForms.informacion_bancaria) {
            params_representantes_status.forms.push(item.persona.bank_references_status);
          }

          if (representanteForms.validacion_identidad) {
            params_representantes_status.other_documents.push(item.doc_validacion_identidad);
          }
        });

        representante_new_status = generateTabLabel(params_representantes_status);
      } else {
        representante_new_status = undefined;
      }
    }

    dispatch(
      setBofuStatusAll({
        status_dictamen: dictamen_new_status,
        status_empresa: empresa_new_status,
        status_aval: aval_new_status,
        status_propietarios_reales: propietario_real_new_status,
        status_garantia: garantia_new_status,
        status_representante: representante_new_status,
      }),
    );
  };

  const generateTabsStructure = async () => {
    try {
      dispatch(setIsLoading(true));
      setIsSuccessData(false);

      const [
        tabs_config,
        loan_application,
        persons,
        documents,
        list_estado_civil,
        list_matrimonial_regimen,
        guarantee_data,
      ] = await Promise.all([
        getTabsConfig({ taxcode: applicationData.type }).unwrap(),
        getLoanAplication(applicationData.id).unwrap(),
        getPersons({
          loanApplicationId: applicationData.id,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
        getEstadoCivil().unwrap(),
        getMatrimonialRegimen().unwrap(),
        getGuaranteesData({ loan_application_id: applicationData.id }).unwrap(),
      ]);

      // identificar si es una solicitud pm o pfae
      const isPFAE = loan_application?.tax_systems?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;

      // validar si la solicitud fue rechazada por documentacion
      const { rechazo_expediente } = STATUS_CATALOG;
      if (loan_application?.status_catalog?.code === rechazo_expediente.code) {
        dispatch(setLoanStatus(rechazo_expediente.code));
        navigate(rechazo_expediente.url);
        return;
      }

      // identificar las personas
      const {
        representante,
        representantes,
        aval,
        conyuges,
        propietarios,
        segundos_avales: lista_avales_adicionales,
        aval_matrimonial_regimen,
      } = identifyPersons(persons, list_estado_civil, list_matrimonial_regimen);

      // obtener los documentos por persona
      const {
        docs_account,
        docs_aval,
        docs_guarantee,
        docs_propietarios,
        docs_representantes,
        docs_avales_adicionales,
        docs_conyuges_avales,
      } = identityDocumentsByPerson({
        documents,
        representante,
        aval,
        client_details: clientDetails,
        propietarios,
        representantes,
        avales_adicionales: lista_avales_adicionales,
        conyuges,
      });

      const { solicitud_credito, validacion_identidad, anexo_1, anexo_2 } = DOCUMENTS_CATALOG;

      const bofu_tabs_obj: BofuTabObject = initializeBofuTabsObject();

      const avales_adicionales: BofuTabRepresentante[] = [];
      let conyuge_aval: PersonaStructure | undefined;

      tabs_config.forEach((tabItem) => {
        // validar que los documentos tengan la estructura correcta
        const valid_tab_documents: BofuTabDocument[] = [];
        tabItem.documents
          .filter((item) => validateDocumentStructure(item))
          .filter((item) => filterDocumentsByGuarantee(item, guarantee_data)) // filtrar los documentos por el tipo de garantia, si es que se requiere
          .forEach((docItem) => {
            const document_value = generateDocumentConfig({
              docItem,
              docs_account,
              docs_aval,
              clientDetailsId: clientDetails.id,
              aval,
            });

            valid_tab_documents.push(document_value);
          });

        // ordernar los documentos por el order
        valid_tab_documents.sort((a, b) => (a.order || 0) - (b.order || 0));

        if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_dictamen.code) {
          bofu_tabs_obj.dictamen.order = tabItem.order;
          bofu_tabs_obj.dictamen.is_active = true;
          bofu_tabs_obj.dictamen.documents = valid_tab_documents;
          bofu_tabs_obj.dictamen.is_disabled = false;

          if (isPFAE) {
            // pfae no tiene dictamen...
            bofu_tabs_obj.dictamen.is_active = false;
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_empresa.code) {
          const doc_solicitud_credito = docs_account.find((item) => item.file_code === solicitud_credito.code);
          bofu_tabs_obj.empresa.order = tabItem.order;
          bofu_tabs_obj.empresa.is_active = true;
          bofu_tabs_obj.empresa.documents = valid_tab_documents;
          bofu_tabs_obj.empresa.forms = mapBofuTabEmpresaForms(tabItem.forms);
          bofu_tabs_obj.empresa.doc_solicitud_credito = doc_solicitud_credito;
          bofu_tabs_obj.empresa.is_disabled = false;

          if (isPFAE) {
            bofu_tabs_obj.empresa.url = "/producto/creditosimple/avance_expediente_digitalPFAE";
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_aval.code) {
          // identificar los documentos para cada tipo de persona
          const documents_aval = valid_tab_documents.filter((item) => item.relationship === "aval");
          const documents_conyuge_aval = valid_tab_documents.filter((item) => item.relationship === "conyuge_aval");

          // atributos para el tab de aval
          bofu_tabs_obj.aval.order = tabItem.order;
          bofu_tabs_obj.aval.is_active = true;
          bofu_tabs_obj.aval.is_disabled = loan_application.decision_validated && aval ? false : true;
          bofu_tabs_obj.aval.forms = mapBofuTabAvalForms(tabItem.forms);

          bofu_tabs_obj.aval.documents = documents_aval;
          bofu_tabs_obj.aval.doc_anexo_1 = aval
            ? findDocumentValue({
                documents: docs_aval,
                file_code: anexo_1.code,
                person_id: aval.id,
              })
            : undefined;
          bofu_tabs_obj.aval.doc_validacion_identidad = aval
            ? findDocumentValue({
                documents: docs_aval,
                file_code: validacion_identidad.code,
                person_id: aval.id,
              })
            : undefined;

          // identificar el conyuge del aval
          conyuge_aval = conyuges.find((item) => item.id === aval?.spouse_id);

          if (conyuge_aval) {
            bofu_tabs_obj.aval.documents_conyuge = generatePropietarioDocs({
              personItem: conyuge_aval,
              valid_documents: documents_conyuge_aval,
              documents_uploaded: docs_conyuges_avales,
              clientDetailsId: clientDetails.id,
            });
            bofu_tabs_obj.aval.doc_anexo_1_conyuge = findDocumentValue({
              documents: docs_conyuges_avales,
              file_code: anexo_1.code,
              person_id: conyuge_aval.id,
            });
            bofu_tabs_obj.aval.doc_validacion_identidad_conyuge = findDocumentValue({
              documents: docs_conyuges_avales,
              file_code: validacion_identidad.code,
              person_id: conyuge_aval.id,
            });

            // identificar si el conyuge del aval tambien es un propietario real, de ser asi no se debe mostrar el formulario del conyuge del aval
            const is_propietario = propietarios.some((propietarioItem) => propietarioItem.id === conyuge_aval?.id);
            bofu_tabs_obj.aval.show_information_form_conyuge = is_propietario ? false : true;
          }

          if (isPFAE) {
            bofu_tabs_obj.aval.is_disabled = false;
            bofu_tabs_obj.aval.show_form_aval = false;

            // si no hay conyuge del aval y no hay segundo aval, se debe desactivar el tab
            if (!conyuge_aval && lista_avales_adicionales.length === 0) {
              bofu_tabs_obj.aval.is_active = false;
            }
          }

          // generar informacion para los avales adicionales
          lista_avales_adicionales.forEach((avalAdicionalItem) => {
            const aval_adicional_documents_with_value = generatePropietarioDocs({
              personItem: avalAdicionalItem,
              valid_documents: documents_aval,
              documents_uploaded: docs_avales_adicionales,
              clientDetailsId: clientDetails.id,
            });

            // generar el objeto de aval adicional
            const new_aval_adicional: BofuTabAvalAdicional = {
              persona: avalAdicionalItem,
              documents: aval_adicional_documents_with_value,
              doc_validacion_identidad: findDocumentValue({
                documents: docs_avales_adicionales,
                file_code: validacion_identidad.code,
                person_id: avalAdicionalItem.id,
              }),
              show_information_form: true,
            };

            // identificar si el aval adicional tiene un conyuge relacionado
            const conyuge_aval_adicional = conyuges.find((item) => item.id === avalAdicionalItem.spouse_id);
            if (conyuge_aval_adicional) {
              console.log("params: ", {
                personItem: conyuge_aval_adicional,
                valid_documents: documents_conyuge_aval,
                documents_uploaded: docs_conyuges_avales,
                clientDetailsId: clientDetails.id,
              });
              const conyuge_aval_adicional_documents_with_value = generatePropietarioDocs({
                personItem: conyuge_aval_adicional,
                valid_documents: documents_conyuge_aval,
                documents_uploaded: docs_conyuges_avales,
                clientDetailsId: clientDetails.id,
              });
              console.log("docs4: ", conyuge_aval_adicional_documents_with_value);

              new_aval_adicional.conyuge = {
                persona: conyuge_aval_adicional,
                matrimonial_regime: avalAdicionalItem.matrimonial_regime?.code,
                documents: conyuge_aval_adicional_documents_with_value || [],
                doc_validacion_identidad: findDocumentValue({
                  documents: docs_conyuges_avales,
                  file_code: validacion_identidad.code,
                  person_id: conyuge_aval_adicional.id,
                }),
                show_information_form: true,
              };
            }

            avales_adicionales.push(new_aval_adicional);
          });
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_propietario.code) {
          bofu_tabs_obj.propietarios_reales.order = tabItem.order;
          bofu_tabs_obj.propietarios_reales.is_active = true;
          bofu_tabs_obj.propietarios_reales.is_disabled = loan_application.decision_validated ? false : true;
          bofu_tabs_obj.propietarios_reales.propietarios = [];

          propietarios.forEach((propietarioItem) => {
            const propietario_documents = generatePropietarioDocs({
              personItem: propietarioItem,
              valid_documents: valid_tab_documents,
              documents_uploaded: docs_propietarios,
              clientDetailsId: clientDetails.id,
            });

            // buscar el anexo 2 del propietario
            const doc_anexo_2 = docs_propietarios.find(
              (item) => item.file_code === anexo_2.code && item.person_id === propietarioItem.id,
            );

            bofu_tabs_obj.propietarios_reales.propietarios.push({
              persona: propietarioItem,
              doc_anexo_2: doc_anexo_2,
              documents: propietario_documents,
              show_information_form: true,
            });
          });

          if (isPFAE) {
            // pfae no tiene propietarios reales...
            bofu_tabs_obj.propietarios_reales.is_active = false;
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_garantia.code) {
          bofu_tabs_obj.garantia.order = tabItem.order;
          bofu_tabs_obj.garantia.is_active = true;
          bofu_tabs_obj.garantia.is_disabled = false;
          bofu_tabs_obj.garantia.items = [];

          guarantee_data.forEach((garantiaItem) => {
            const guarantee_documents = generateGuaranteeDocs({
              clientDetailsId: clientDetails.id,
              guaranteeItem: garantiaItem,
              valid_documents: valid_tab_documents,
              documents_uploaded: docs_guarantee,
            });

            if (guarantee_documents.length > 0) {
              bofu_tabs_obj.garantia.items.push({
                guarantee: garantiaItem,
                documents: guarantee_documents,
              });
            }
          });

          // ordenar por 2 parametros: deben tener documentos y orden alfabetico
          bofu_tabs_obj.garantia.items.sort((a, b) => {
            if (a.documents.length === 0 && b.documents.length > 0) {
              return 1;
            }
            if (a.documents.length > 0 && b.documents.length === 0) {
              return -1;
            }
            return a.guarantee.guarantee_type?.name?.localeCompare(b.guarantee.guarantee_type?.name || "");
          });
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_representante.code) {
          bofu_tabs_obj.representantes.order = tabItem.order;
          bofu_tabs_obj.representantes.is_active = true;
          bofu_tabs_obj.representantes.documents = valid_tab_documents;
          bofu_tabs_obj.representantes.forms = mapBofuTabRepresentanteForms(tabItem.forms);
          bofu_tabs_obj.representantes.items = [];

          if (isPFAE) {
            // pfae no tiene dictamen, el RL se crea en el tofu
            bofu_tabs_obj.representantes.is_disabled = false;
          } else {
            bofu_tabs_obj.representantes.is_disabled = loan_application.decision_validated ? false : true;
          }

          representantes.forEach((representanteItem) => {
            const representante_documents = generatePropietarioDocs({
              personItem: representanteItem,
              valid_documents: valid_tab_documents,
              documents_uploaded: docs_representantes,
              clientDetailsId: clientDetails.id,
            });

            const doc_validacion_identidad = docs_representantes.find(
              (item) => item.file_code === validacion_identidad.code && item.person_id === representanteItem.id,
            );

            bofu_tabs_obj.representantes.items.push({
              persona: representanteItem,
              documents: representante_documents,
              show_information_form: true,
              doc_validacion_identidad,
            });
          });
        }
      });

      // Jerarquia de Representante Legal:
      //   1. si el RL es aval, se muestra el formulario de aval
      //   2. si el RL es propietario real, se muestra el formulario de propietario real
      //   3. si el RL es conyuge del aval, se muestra el formulario de conyuge del aval
      // Jerarquia de Propietarios Reales:
      //   1. si el PR es un aval o propietario real no se debe mostrar el formulario
      if (bofu_tabs_obj.representantes.is_active) {
        bofu_tabs_obj.representantes.items.forEach((representanteItem) => {
          const is_aval = aval?.id === representanteItem.persona.id;
          const is_propietario = propietarios.some(
            (propietarioItem) => propietarioItem.id === representanteItem.persona.id,
          );
          const is_conyuge_aval = conyuge_aval?.id === representanteItem.persona.id;
          representanteItem.show_information_form = is_aval || is_propietario || is_conyuge_aval ? false : true;
        });
      }

      // si el PR es un aval no se debe mostrar el formulario
      if (bofu_tabs_obj.propietarios_reales.is_active) {
        bofu_tabs_obj.propietarios_reales.propietarios.forEach((propietarioItem) => {
          const is_aval = aval?.id === propietarioItem.persona.id;
          propietarioItem.show_information_form = is_aval ? false : true;
        });
      }

      dispatch(setBofuTabsObj(bofu_tabs_obj));
      dispatch(setLoanApplication(loan_application));
      dispatch(setRepresentanteLegal(representante));
      dispatch(setAval(aval));
      dispatch(setAvalesAdicionales(avales_adicionales));
      dispatch(setAvalConyuge(conyuge_aval));
      dispatch(setAvalRegimenConyugal(aval_matrimonial_regimen));
      setIsSuccessData(true);
      dispatch(setIsLoading(false));
    } catch (error) {
      console.error("Ha ocurrido un error al el bofu", error);
      Sentry.captureException(error, {
        extra: {
          component: "TabBofu-generateTabsStructure",
        },
      });
    } finally {
      // dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    generateTabsStatusLabel();
    // sendToDictamenJuridico();
  }, [
    bofuTabsObj.dictamen.documents,
    bofuTabsObj.empresa.documents,
    bofuTabsObj.empresa.forms,
    bofuTabsObj.aval.documents,
    bofuTabsObj.aval.documents_conyuge,
    bofuTabsObj.aval.forms,
    bofuTabsObj.propietarios_reales.propietarios,
    bofuTabsObj.garantia.items,
    bofuTabsObj.representantes.items,
    bofuTabsObj.representantes.forms,
    applicationData.decision_validated,
    personConyugeAval,
    avalesAdicionales,
  ]);

  useEffect(() => {
    generateTabsStructure();
  }, [location.pathname]);

  useEffect(() => {
    generateSolicitudCredito();
  }, [isLoading, bofuTabsObj, applicationData]);

  return (
    <>
      {isSuccessData ? (
        <Box
          sx={{
            width: "100%",
            height: "80px",
            bgcolor: theme?.palette?.primary?.base,
            p: 1,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Grid container spacing={1} display="flex" justifyContent="center">
            {Object.values(bofuTabsObj)
              .filter((tab) => tab.is_active)
              .sort((a, b) => a.order - b.order)
              .map((tab) => (
                <Grid item xs={2} md={2} key={tab.code}>
                  <TabBofuButton key={tab.code} bofuTab={tab} />
                </Grid>
              ))}
          </Grid>
        </Box>
      ) : (
        <Skeleton variant="rectangular" sx={{ height: "90px", width: "100%", borderRadius: 2 }} />
      )}
    </>
  );
};

export default TabBofu;
